/**
 * Global Prefetch Context
 *
 * Manages global data prefetch state and triggers prefetch on login
 * - Triggers automatically when Gaushala user logs in
 * - Checks cache age before prefetching
 * - Exposes prefetch state to entire app
 */

import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { useGlobalDataPrefetch } from '../hooks/useGlobalDataPrefetch';
import { useAuth } from './AuthContext';

interface GlobalPrefetchContextType {
  isLoading: boolean;
  progress: number;
  currentTask: string;
  error: string | null;
  isComplete: boolean;
  isBlocking: boolean;
  startBackgroundPrefetch: () => void;
  needsPrefetch: () => boolean;
  forcePrefetch: () => void;
}

const GlobalPrefetchContext = createContext<GlobalPrefetchContextType | undefined>(undefined);

/**
 * GlobalPrefetchProvider component
 */
export function GlobalPrefetchProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const prefetch = useGlobalDataPrefetch();

  /**
   * Trigger prefetch only AFTER user logs in (JWT token is guaranteed to exist)
   */
  useEffect(() => {
    if (!user) {
      console.log('[GlobalPrefetch] Waiting for user login...');
      return;
    }

    // User is logged in - verify JWT token exists in localStorage
    const token = localStorage.getItem('saubhagya_jwt_token');
    if (!token) {
      console.log('[GlobalPrefetch] User logged in but JWT not yet in localStorage, waiting...');
      return;
    }

    // JWT token exists - trigger prefetch if needed
    console.log('[GlobalPrefetch] User logged in with JWT token - triggering prefetch check');
    if (prefetch.needsPrefetch()) {
      console.log('[GlobalPrefetch] Cache is stale - starting background prefetch');
      prefetch.startBackgroundPrefetch();
    } else {
      console.log('[GlobalPrefetch] Cache is fresh - prefetch not needed');
    }
  }, [user, prefetch.needsPrefetch, prefetch.startBackgroundPrefetch]);

  return (
    <GlobalPrefetchContext.Provider
      value={{
        isLoading: prefetch.isLoading,
        progress: prefetch.progress,
        currentTask: prefetch.currentTask,
        error: prefetch.error,
        isComplete: prefetch.isComplete,
        isBlocking: prefetch.isBlocking,
        startBackgroundPrefetch: prefetch.startBackgroundPrefetch,
        needsPrefetch: prefetch.needsPrefetch,
        forcePrefetch: prefetch.forcePrefetch,
      }}
    >
      {children}
    </GlobalPrefetchContext.Provider>
  );
}

/**
 * Hook to use global prefetch context
 */
export function useGlobalPrefetch(): GlobalPrefetchContextType {
  const context = useContext(GlobalPrefetchContext);
  if (!context) {
    throw new Error('useGlobalPrefetch must be used within GlobalPrefetchProvider');
  }
  return context;
}
